import { useState, useEffect, useMemo } from 'react';
import normesService from '../services/normesService';

/**
 * Hook pour gérer les normes et les filtres en cascade
 *
 * @param {Object} [options]
 * @param {Set<string>|Array<string>|null} [options.allowedNormeIds]
 *   Restreint la liste des normes affichées à ces IDs (ex: normes cochées
 *   dans l'étape 2 du rapport). `null`/`undefined` = pas de filtre.
 * @returns {Object} État et méthodes pour la sélection de templates
 */
export function useNormesTemplates(options = {}) {
  const { allowedNormeIds = null } = options;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Liste des normes disponibles
  const [normes, setNormes] = useState([]);
  
  // Templates de la norme sélectionnée
  const [normeData, setNormeData] = useState(null);
  
  // Filtres en cascade
  const [selectedDomaine, setSelectedDomaine] = useState('BT'); // 'BT' | 'HT'
  const [selectedNormeId, setSelectedNormeId] = useState('');
  const [selectedParagraphe, setSelectedParagraphe] = useState('');
  const [selectedCategorie, setSelectedCategorie] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Charger la liste des normes au montage
  useEffect(() => {
    const loadNormes = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await normesService.getListeNormes();
        console.log('📥 Hook - Data reçue du service:', data);
        // ✅ Le service retourne directement un tableau de normes
        setNormes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('❌ Erreur chargement liste normes:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadNormes();
  }, []);

  // Charger les templates quand une norme est sélectionnée
  useEffect(() => {
    if (!selectedNormeId) {
      setNormeData(null);
      return;
    }

    const loadTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await normesService.getNormeTemplates(selectedNormeId);
        console.log('📥 Hook - Templates reçus:', data);
        setNormeData(data);
      } catch (err) {
        console.error('❌ Erreur chargement templates:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [selectedNormeId]);

  // Extraire les paragraphes uniques de la norme sélectionnée
  const paragraphes = useMemo(() => {
    if (!normeData?.templates) return [];
    
    const uniqueParagraphes = [];
    const seen = new Set();
    
    normeData.templates.forEach(t => {
      // ✅ Utiliser paragraphe_code (format API Base44)
      const key = `${t.paragraphe_code}|||${t.paragraphe_titre}`;
      if (!seen.has(key) && t.paragraphe_code) {
        seen.add(key);
        uniqueParagraphes.push({
          code: t.paragraphe_code,
          titre: t.paragraphe_titre || t.paragraphe_code
        });
      }
    });
    
    return uniqueParagraphes.sort((a, b) => {
      // Tri numérique si possible
      const aNum = parseInt(a.code);
      const bNum = parseInt(b.code);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.code.localeCompare(b.code);
    });
  }, [normeData]);

  // Extraire les catégories du paragraphe sélectionné
  const categories = useMemo(() => {
    if (!normeData?.templates || !selectedParagraphe) return [];
    
    // ✅ Utiliser paragraphe_code
    const filteredTemplates = normeData.templates.filter(
      t => t.paragraphe_code === selectedParagraphe
    );
    
    const uniqueCategories = [];
    const seen = new Set();
    
    filteredTemplates.forEach(t => {
      // ✅ Utiliser categorie_code
      const key = `${t.categorie_code}|||${t.categorie_titre}`;
      if (!seen.has(key) && t.categorie_code) {
        seen.add(key);
        uniqueCategories.push({
          code: t.categorie_code,
          titre: t.categorie_titre || t.categorie_code
        });
      }
    });
    
    return uniqueCategories.sort((a, b) => a.code.localeCompare(b.code));
  }, [normeData, selectedParagraphe]);

  // Extraire les templates de la catégorie sélectionnée
  const templates = useMemo(() => {
    if (!normeData?.templates || !selectedParagraphe || !selectedCategorie) return [];
    
    // ✅ Utiliser paragraphe_code et categorie_code
    return normeData.templates.filter(
      t => t.paragraphe_code === selectedParagraphe && 
           t.categorie_code === selectedCategorie
    ).sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
  }, [normeData, selectedParagraphe, selectedCategorie]);

  // Réinitialiser les sélections en cascade (on garde le domaine sélectionné)
  const resetFilters = () => {
    setSelectedNormeId('');
    setSelectedParagraphe('');
    setSelectedCategorie('');
    setSelectedTemplate(null);
    setNormeData(null);
  };

  // Handler pour changement de norme
  const handleNormeChange = (normeId) => {
    setSelectedNormeId(normeId);
    setSelectedParagraphe('');
    setSelectedCategorie('');
    setSelectedTemplate(null);
  };

  // Handler pour changement de domaine (BT/HT) — réinitialise la cascade
  const handleDomaineChange = (domaine) => {
    setSelectedDomaine(domaine);
    setSelectedNormeId('');
    setSelectedParagraphe('');
    setSelectedCategorie('');
    setSelectedTemplate(null);
    setNormeData(null);
  };

  // Handler pour changement de paragraphe
  const handleParagrapheChange = (paragraphe) => {
    setSelectedParagraphe(paragraphe);
    setSelectedCategorie('');
    setSelectedTemplate(null);
  };

  // Handler pour changement de catégorie
  const handleCategorieChange = (categorie) => {
    setSelectedCategorie(categorie);
    setSelectedTemplate(null);
  };

  // Handler pour sélection de template
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  // Statistiques
  const stats = useMemo(() => {
    return {
      totalNormes: normes.length,
      normesActives: normes.filter(n => n.actif !== false).length,
      templatesCount: normeData?.templates?.length || 0,
      paragraphesCount: paragraphes.length,
      categoriesCount: categories.length,
      currentTemplatesCount: templates.length
    };
  }, [normes, normeData, paragraphes, categories, templates]);

  // Normes filtrées par :
  //  - actif (aligne avec app web)
  //  - domaine sélectionné (BT/HT)
  //  - allowedNormeIds (= normes cochées dans l'étape 2 du rapport, si fourni)
  const allowedSet = useMemo(() => {
    if (!allowedNormeIds) return null;
    const list = Array.isArray(allowedNormeIds) ? allowedNormeIds : Array.from(allowedNormeIds);
    if (list.length === 0) return null;
    return new Set(list.map((id) => String(id)));
  }, [allowedNormeIds]);

  const visibleNormes = useMemo(() => {
    return normes.filter((n) => {
      if (n.actif === false) return false;
      if (selectedDomaine && (n.domaine || 'BT') !== selectedDomaine) return false;
      if (allowedSet && !allowedSet.has(String(n.id))) return false;
      return true;
    });
  }, [normes, selectedDomaine, allowedSet]);

  return {
    // État
    loading,
    error,
    
    // Listes
    normes: visibleNormes,
    paragraphes,
    categories,
    templates,
    
    // Sélections actuelles
    selectedDomaine,
    selectedNormeId,
    selectedParagraphe,
    selectedCategorie,
    selectedTemplate,
    
    // Info norme sélectionnée
    normeInfo: normeData?.norme || null,
    
    // Handlers
    handleDomaineChange,
    handleNormeChange,
    handleParagrapheChange,
    handleCategorieChange,
    handleTemplateSelect,
    resetFilters,
    
    // Stats
    stats
  };
}

export default useNormesTemplates;